import dayjs from 'dayjs';
import { useUnit } from 'effector-react';
import $$api from 'models/api';
import navigate from 'models/router-gate';
import { $global } from 'models/store-global';
import { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

import HText from 'components/HText';
import TemplateEmailModal from 'components/PopUps/TemplateEmailModal';
import CommunityTab from 'components/StyledTemplates/CommunityTab';
import { Frame, RowWrapper } from 'components/StyledTemplates/StyledTemplates';
import { AssignedRolesText } from 'components/pages/Dashboard/common/CommunityPageStyles';

import AlertsService from 'services/AlertsService';
import DocumentDownloadService from 'services/DocumentDownloadService';
import ModalsService from 'services/ModalsService';

import useDocumentTitle from 'hooks/useDocumentTitle';
import useResources from 'hooks/useResources';

import { type AssignedRole } from 'types/zod/assigned-role';

const CommunityAssignedRolesPage = () => {
    const { t } = useResources();
    const path = useLocation().pathname.split('/').slice(1);
    const selected = path[2];

    const assigned = useUnit($global.map((state) => state.community?.assigned || []));
    const selectedRole = assigned.find((item) => +item.participant_id === +selected);
    const documents = selectedRole && selectedRole.roles ? selectedRole.roles : [];

    const handlers = {
        printDocuments: (role: AssignedRole) => async () => {
            await DocumentDownloadService.download(role?.document_id);
        },
        printScans: (role: AssignedRole) => async () => {
            const promises = (role.scan_names || []).map((fileName) =>
                $$api.documents.downloadScanFx({ documentId: role.document_id, filename: fileName }),
            );
            try {
                await Promise.all(promises);
            } catch (error) {
                AlertsService.handleError(error);
            }
        },
        openTempEmailModal: (role: AssignedRole) => async () => {
            // TODO: Get rid of TEMPLATE_EMAIL modal. User modality instead
            ModalsService.showModal('TEMPLATE_EMAIL', role?.participant_role_id);
        },
    };

    useEffect(() => {
        if (!assigned.map((item) => +item.participant_id).includes(+selected)) {
            navigate(`/`);
        }
    }, []);

    useDocumentTitle(`Assigned Roles`);

    return (
        <>
            <TemplateEmailModal />

            <CommunityTab
                title={t(`communityPage.assigned_title`, { name: selectedRole?.full_name })}
                description={t(`communityPage.assigned_descriptions`)}
                closable={false}
            >
                {documents.map((role, index) => {
                    const date = dayjs(role.document_last_updated_timestamp).format('MM-DD-YYYY');
                    const statusCode =
                        role.document_status_code === 'Executed'
                            ? t('document_status_code.Completed')
                            : t(`document_status_code.${role.document_status_code}`);
                    return (
                        <CommunityTab key={index} closable={false} extra="width: 100%;">
                            <RowWrapper>
                                <AssignedRolesText>{`${t(`participantRole.${role.role_type_code}`)} - ${
                                    role.document_name
                                } (${statusCode} ${date})`}</AssignedRolesText>
                                <Frame extra="flex-direction: row;">
                                    <Frame>
                                        {role.visibility_code === 'full_doc' && (
                                            <AssignedRolesText lower={true} onClick={handlers.printDocuments(role)}>
                                                {t(`communityPage.assigned_ViewCreatedDocument`)}
                                            </AssignedRolesText>
                                        )}
                                        {role.visibility_code === 'full_doc' &&
                                            role.scan_names &&
                                            role.scan_names.length > 0 && (
                                                <AssignedRolesText lower={true} onClick={handlers.printScans(role)}>
                                                    {t(`communityPage.assigned_ViewUploadedDocument`)}
                                                </AssignedRolesText>
                                            )}
                                    </Frame>
                                    <Button
                                        data-qaid="learn_more_button"
                                        variant="secondary"
                                        onClick={handlers.openTempEmailModal(role)}
                                    >
                                        {t(`communityPage.assigned_LearnMore`)}
                                    </Button>
                                </Frame>
                            </RowWrapper>
                        </CommunityTab>
                    );
                })}
                {!!selectedRole?.documentsLocation && (
                    <div style={{ display: 'flex', alignSelf: 'flex-start' }}>
                        <HText>
                            {t('communityPage.assigned_documents_location', {
                                location: selectedRole.documentsLocation,
                            })}
                        </HText>
                    </div>
                )}
            </CommunityTab>
        </>
    );
};

export default CommunityAssignedRolesPage;
